import staffPermissionsService from '../services/staffPermissionsService';

const state = {
  loadedPermissions: {}, // { permissionKey: permissionData }
  requestedPermissions: {}, // { permissionKey: Promise }
};

const mutations = {
  setRequestedPermission(state, { key, promise }) {
    state.requestedPermissions[key] = promise;
  },
  setLoadedPermission(state, { key, data }) {
    state.loadedPermissions[key] = data;
    delete state.requestedPermissions[key];
  },
};

const actions = {
  async loadPermission({ state, commit }, { permissionKey }) {
    return new Promise((resolve, reject) => {
      // If permission is already being requested, wrap the existing promise
      if (state.requestedPermissions[permissionKey]) {
        state.requestedPermissions[permissionKey]
          .then(resolve)
          .catch(reject);
        return;
      }

      // If permission is already loaded, resolve immediately
      if (state.loadedPermissions[permissionKey]) {
        resolve(state.loadedPermissions[permissionKey]);
        return;
      }

      // Create new request for the permission
      const permissionPromise = staffPermissionsService.getStaffPermissions([permissionKey])
        .then((response) => {
          const permissionData = response.find((item) => item.key === permissionKey);
          commit('setLoadedPermission', { key: permissionKey, data: permissionData });
          resolve(permissionData);
        })
        .catch((error) => {
          // Clean up the requested permission on error
          delete state.requestedPermissions[permissionKey];
          reject(error);
        });

      // Store the promise in requestedPermissions
      commit('setRequestedPermission', { key: permissionKey, promise: permissionPromise });
    });
  },

  async loadPermissions({ state, commit }, { permissionKeys }) {
    const uniqueKeys = [...new Set(permissionKeys)];
    const keysToLoad = uniqueKeys.filter((key) => !state.loadedPermissions[key] && !state.requestedPermissions[key]);

    // Load new permissions if needed
    if (keysToLoad.length > 0) {
      const loadPromise = staffPermissionsService.getStaffPermissions(keysToLoad)
        .then((response) => {
          response.forEach((permissionData) => {
            commit('setLoadedPermission', { key: permissionData.key, data: permissionData });
          });
          return response;
        })
        .catch((error) => {
          keysToLoad.forEach((key) => delete state.requestedPermissions[key]);
          throw error;
        });

      keysToLoad.forEach((key) => {
        commit('setRequestedPermission', { key, promise: loadPromise });
      });
    }

    // Wait for all permissions to be available and map results
    const permissionPromises = uniqueKeys.map((key) => {
      if (state.loadedPermissions[key]) {
        return Promise.resolve(state.loadedPermissions[key]);
      }
      // Extract the individual permission from the array when the promise resolves
      return state.requestedPermissions[key].then((response) => response.find((item) => item.key === key));
    });

    const resolvedPermissions = await Promise.all(permissionPromises);

    // Map original keys (including duplicates) to their results
    return permissionKeys.map((key) => {
      const uniqueIndex = uniqueKeys.indexOf(key);
      return resolvedPermissions[uniqueIndex];
    });
  },
};

const getters = {
  isPermissionLoading: (state) => (permissionKey) => !!state.requestedPermissions[permissionKey],
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
